import React, { FC, useMemo, useState } from "react";
import { COLOR, Box, Layout as AuroraLayout, useDevice, View, Divider } from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import { Button, Layout, Text } from "@lookiero/sty-psp-ui";
import { Order as OrderEntity, ProductWithFeedback } from "../../../../../domain/entities/order/Order";
import { Product } from "../../../../../domain/entities/product/Product";
import { Question } from "../../../../../domain/entities/question/Question";
import { ProductList } from "../../../components/productList/ProductList";
import { DOMAIN, I18nMessages } from "../../../i18n/i18n";
import { OrderHeader } from "../header/orderHeader/OrderHeader";
import { style as orderStyle } from "./Order.styles";
import { FeedbackPanel } from "./feedbackPanel/FeedbackPanel";
import { OrderBanners } from "./orderBanners/OrderBanners";
import { ReturnInfo } from "./returnInfo/ReturnInfo";
import { ReturnPanel } from "./returnPanel/ReturnPanel";
import { TotalTable } from "./totalTable/TotalTable";

interface OrderProps {
  readonly layout: Layout;
  readonly date: string;
  readonly faqUrl: string;
  readonly isCancelled: boolean;
  readonly order: OrderEntity | undefined;
  readonly originalPriceAmount: number;
  readonly promoAmount: number;
  readonly questions: Record<string, Question[]>;
  readonly returnPrice: number;
  readonly shippingAddress: string;
  readonly showExpired: boolean;
  readonly showRefundNotification: boolean; // (expectingRefund)= hasReturnedItems
  readonly showReturnButton: boolean; //  = inProgress && isDelivered && hasSelectedItems && !hasToReturnItems (only one returing at a time?)
  readonly showReturnInstructions: boolean; // = !hasReturnedItems && hasToReturnItems --> hasToReturnItems ???
  readonly unavailableItemsPrice: number;
  readonly onBack: () => void;
  readonly onFinishReturn: () => void;
  readonly onPressReturnInstructions: () => void | undefined;
  readonly onPressOrderProductImage: (product: Product) => void;
}

const Order: FC<OrderProps> = ({
  layout: Layout,
  order,
  returnPrice,
  unavailableItemsPrice,
  originalPriceAmount,
  promoAmount,
  shippingAddress,
  questions,
  faqUrl,
  isCancelled,
  showExpired,
  showRefundNotification,
  showReturnButton,
  showReturnInstructions,
  date,
  onFinishReturn,
  onPressReturnInstructions,
  onPressOrderProductImage,
  onBack,
}) => {
  // We need to manage the selected items in this point because we need to pass the array to the feedback panel
  const [selectedItems, setSelectedItems] = useState<ProductWithFeedback[]>([]);
  const [showReturnPanel, setShowReturnPanel] = useState(false);
  const [showFeedbackPanel, setShowFeedbackPanel] = useState(false);

  const orderHeaderText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.ORDER_HEADER,
    values: { refOrder: order ? order.reference : "" },
  });
  const orderReturnItemsCtaText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.ORDER_RETURN_ITEMS_CTA,
  });
  const orderConfirmatedDateText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.ORDER_CONFIRMATED_DATE,
  });
  const orderAddressText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.ORDER_ADDRESS,
  });

  const { screen } = useDevice();
  const isDesktopScreen = screen.L;

  const style = useMemo(() => orderStyle(), []);

  return (
    <>
      <Layout footer={null} header={<OrderHeader title={orderHeaderText} onBack={onBack} />}>
        {order ? (
          <View style={style.wrapper}>
            <AuroraLayout fullWidth={!isDesktopScreen} style={isDesktopScreen ? style.layout : undefined}>
              <Box size={{ L: "2/3" }} style={isDesktopScreen ? style.detailWrapperLarge : undefined}>
                <View style={isDesktopScreen ? style.detailLarge : style.detailSmall}>
                  {!isDesktopScreen ? (
                    <View style={style.smallBannerWrapper}>
                      <OrderBanners showExpired={showExpired} showRefundNotification={showRefundNotification} />
                    </View>
                  ) : null}
                  {order.items && (
                    <ProductList crossed={isCancelled} products={order.items} onPressImage={onPressOrderProductImage} />
                  )}
                  {showReturnButton && !isDesktopScreen ? (
                    <View style={style.smallReturnButton}>
                      <Button
                        style={{ button: [style.fullWidthButton] }}
                        testID="return-order-button"
                        variant="primary"
                        onPress={() => {
                          setShowReturnPanel(true);
                        }}
                      >
                        {orderReturnItemsCtaText}
                      </Button>
                    </View>
                  ) : null}
                  {!isDesktopScreen ? (
                    <ReturnInfo
                      faqUrl={faqUrl}
                      isCancelled={isCancelled}
                      showRefundNotification={showRefundNotification}
                      showReturnInstructions={showReturnInstructions}
                      style={style.smallReturnInfo}
                      onPressReturnInstructions={onPressReturnInstructions}
                    />
                  ) : null}
                </View>
              </Box>

              <Box
                size={{ L: "1/3" }}
                style={[style.resumeWrapper, isDesktopScreen ? style.resumeWrapperLarge : undefined]}
              >
                {(!isCancelled || showReturnInstructions) && (
                  <View>
                    {isDesktopScreen ? (
                      <>
                        <OrderBanners
                          showExpired={showExpired}
                          showRefundNotification={showRefundNotification}
                          style={style.largeBanner}
                        />
                        <ReturnInfo
                          faqUrl={faqUrl}
                          isCancelled={isCancelled}
                          showRefundNotification={showRefundNotification}
                          showReturnInstructions={showReturnInstructions}
                          onPressReturnInstructions={onPressReturnInstructions}
                        />
                        <Divider color={COLOR.BG_PRIMARY_MEDIUM_LIGHT} />
                      </>
                    ) : null}
                  </View>
                )}
                <View style={isDesktopScreen ? style.info : undefined}>
                  <View style={style.infoBox}>
                    <Text level={3} style={style.subtitle} variant="action">
                      {orderConfirmatedDateText}
                    </Text>
                    <Text level={3}>{date}</Text>
                  </View>
                  <View style={style.infoBox}>
                    <Text level={3} style={style.subtitle} variant="action">
                      {orderAddressText}
                    </Text>
                    <Text level={3}>{shippingAddress}</Text>
                  </View>

                  <View>
                    <TotalTable
                      amountToDiscount={promoAmount}
                      initialPrice={order.initialPrice || 0}
                      itemsCount={order.items.length}
                      originalTotalPrice={originalPriceAmount}
                      promo={order.promo}
                      returnedTotalPrice={returnPrice}
                      totalPrice={order.totalPrice || 0}
                      unavailableItemsPrice={unavailableItemsPrice}
                    />
                  </View>

                  {showReturnButton && isDesktopScreen ? (
                    <Button
                      style={{ button: [style.returnButton, style.fullWidthButton] }}
                      testID="return-order-button"
                      variant="primary"
                      onPress={() => {
                        setShowReturnPanel(true);
                      }}
                    >
                      {orderReturnItemsCtaText}
                    </Button>
                  ) : null}
                </View>
              </Box>
            </AuroraLayout>
          </View>
        ) : null}
      </Layout>

      {order && showReturnPanel ? (
        <>
          <ReturnPanel
            currentOrder={order}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            onBack={() => {
              setSelectedItems([]);
              setShowReturnPanel(false);
            }}
            onNext={() => {
              setShowFeedbackPanel(true);
            }}
          />

          {showFeedbackPanel ? (
            <FeedbackPanel
              currentOrder={order}
              questions={questions}
              selectedItems={selectedItems}
              onNext={onFinishReturn}
              onBack={() => {
                setShowFeedbackPanel(false);
              }}
            />
          ) : null}
        </>
      ) : null}
    </>
  );
};

export { Order };
